import { useToast } from '@/composables/useToast';
import { getToken } from '@/utils/token';
import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { startLoading, stopLoading } from './request-loading';

const { openToast } = useToast();

/**
 * 直播微服務（go-livestream）專用的 axios instance。
 *
 * 與 `axios-instance` 分開的三個理由：
 * 1. 回應沒有 `{isSuccess, errorMessage, content}` 信封，直接就是資料本身。
 * 2. 大量使用 204 No Content（關播、取消、踢出、提醒、下台），
 *    共用 instance 的攔截器會對非 200/201 一律跳錯誤 toast。
 * 3. 401 與「來源不允許」的 403 是 `text/plain`，由中介層直接回，
 *    業務錯誤才是 `{"error": "說明"}` —— 兩者要分開解析。
 */

/** 直播微服務的錯誤。`correlationId` 可直接拿去請後端撈 log。 */
export class GoLiveStreamError extends Error {
  readonly status: number;
  readonly correlationId: string | null;

  constructor(message: string, status: number, correlationId: string | null) {
    super(message);
    this.name = 'GoLiveStreamError';
    this.status = status;
    this.correlationId = correlationId;
  }
}

const DEFAULT_MESSAGE: Record<number, string> = {
  400: '請求內容不正確',
  401: '尚未登入或登入已過期',
  403: '沒有執行此操作的權限',
  404: '查無此直播',
  409: '目前狀態無法執行此操作',
  500: '直播系統忙線中，請稍後再試'
};

/**
 * 從回應取出可顯示的錯誤訊息。
 *
 * 業務錯誤是 JSON `{"error": "說明"}`；401 與 403（來源不允許）
 * 是 `text/plain`，直接對它做 `.json()` 會炸。
 */
function resolveErrorMessage(status: number, data: unknown, fallback: string): string {
  if (typeof data === 'string' && data.trim()) {
    return data.trim();
  }

  if (data && typeof data === 'object' && 'error' in data) {
    const detail = (data as { error?: unknown }).error;
    if (typeof detail === 'string' && detail.trim()) {
      return detail.trim();
    }
  }

  return DEFAULT_MESSAGE[status] ?? fallback;
}

const baseUrl = import.meta.env.VITE_GOLIVESTREAM_BASE_URL;

export const GOLIVESTREAM_ORIGIN = baseUrl?.replace(/\/+$/, '') ?? '';

const goLiveStreamAxios = axios.create({
  baseURL: `${GOLIVESTREAM_ORIGIN}/api/v1`,
  timeout: 30000,
  // 後端接受 ?speakerId=1&speakerId=2，axios 預設會序列化成 speakerId[]=1
  paramsSerializer: { indexes: null }
});

goLiveStreamAxios.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (!config.headers?.disableGlobalLoading) startLoading();

    config.headers['Authorization'] = `Bearer ${getToken()}`;

    return config;
  },
  (error: AxiosError) => {
    console.error('[go-livestream] request error:', error);
    return Promise.reject(error);
  }
);

goLiveStreamAxios.interceptors.response.use(
  (response) => {
    // 204 是正常結果，不是錯誤 —— 關播、取消、踢出、提醒、下台都回 204
    stopLoading();
    return response;
  },
  (error: AxiosError) => {
    const { config, message, response } = error;
    stopLoading();

    const status = response?.status ?? 0;
    const correlationId = (response?.headers?.['x-correlation-id'] as string) ?? null;
    const detail = resolveErrorMessage(status, response?.data, message);

    console.error('[go-livestream] request failed', {
      url: config?.url,
      status,
      correlationId,
      detail
    });

    // 401 只讓這一次請求失敗，**不會**把使用者登出。
    //
    // 登入狀態屬於中央認證伺服器，由 `axios-instance` 那邊處理；
    // 直播微服務拒收 token 的原因可能與 session 是否有效無關
    // （例如該環境還沒接上認證、或 token 的 audience 不符），
    // 讓一支背景請求把整個 app 登出會造成登入後立刻被踢出的迴圈。
    if (config?.headers?.['X-Silent-Error'] !== 'true') {
      openToast('error', detail);
    }

    return Promise.reject(new GoLiveStreamError(detail, status, correlationId));
  }
);

export default goLiveStreamAxios;
